import { Tags } from "@/enum/tags";
import { apiSlice } from "../http";
import { ApiResponse, DtefPagination, SameFileImport } from "@/@types/dtfe-import";

export interface DuplicateCheckResponse {
  hasDuplicate: boolean;
  existingCount: number;
  sameFileImports: SameFileImport[];
}

export interface DtefImportAuditEvent {
  id: number;
  entity_type: string;
  entity_id: string;
  event_type: string;
  event_payload: {
    domain?: string;
    operation?: string;
    total_items?: number;
    successful?: number;
    failed?: number;
    metadata?: {
      fileName?: string;
      s3Key?: string;
      batchId?: string;
      action?: string;
    };
  };
  email: string;
  created_at: string;
}

export interface DtefImportHistoryResponse {
  data: DtefImportAuditEvent[];
  pagination: DtefPagination;
}

export interface DtefImportHistoryItem extends DtefImportAuditEvent {
  is_active: boolean;
  active_count: number;
  deleted_by?: string;
  deleted_at?: string;
}

export interface DtefImportHistoryUnifiedResponse {
  data: DtefImportHistoryItem[];
  pagination: DtefPagination;
}

export type DtefImportHistoryType = "all" | "pix" | "payment";
export type DtefImportHistoryStatus = "all" | "active" | "deleted";
export type DtefImportHistoryOrderBy =
  | "created_at"
  | "email"
  | "file_name"
  | "total_items";
export type DtefImportHistoryOrderDir = "asc" | "desc";

export interface DtefImportHistoryQuery {
  page?: number;
  limit?: number;
  type?: DtefImportHistoryType;
  dateFrom?: string;
  dateTo?: string;
  actorEmail?: string;
  fileName?: string;
  status?: DtefImportHistoryStatus;
  orderBy?: DtefImportHistoryOrderBy;
  orderDir?: DtefImportHistoryOrderDir;
}

export interface DtefPaymentDetail {
  store_code: number;
  authorization: string;
  brand: string;
  type_operation: string;
  quantity: number;
  total_amount: string;
  date_operation: string;
}

export interface PixDtefDetail {
  store_code: number;
  total_amount: string;
  quantity: number;
}

export interface BatchDetailsResponse {
  dtef_payment: DtefPaymentDetail[];
  pix_dtef: PixDtefDetail[];
}

export const dtefImportApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    importDtefFile: builder.mutation<ApiResponse, { file: File; action?: string }>({
      query: ({ file, action }) => {
        const formData = new FormData();
        formData.append("files", file);
        if (action) formData.append("action", action);

        return {
          url: `/dtef/upload`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [Tags.DTEF],
    }),
    importPixDtefFile: builder.mutation<ApiResponse, { file: File; action?: string }>({
      query: ({ file, action }) => {
        const formData = new FormData();
        formData.append("files", file);
        if (action) formData.append("action", action);

        return {
          url: `/dtef/pix/upload`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [Tags.DTEF],
    }),
    checkDtefDuplicate: builder.mutation<
      DuplicateCheckResponse,
      { dateOperation: string; fileName?: string }
    >({
      query: (body) => ({
        url: `/dtef/check-duplicate`,
        method: "POST",
        body,
      }),
    }),
    checkPixDtefDuplicate: builder.mutation<
      DuplicateCheckResponse,
      { dateOperation: string; fileName?: string }
    >({
      query: (body) => ({
        url: `/dtef/pix/check-duplicate`,
        method: "POST",
        body,
      }),
    }),
    getDtefImportHistory: builder.query<
      DtefImportHistoryResponse,
      { entityType: string; page?: number; limit?: number; dateFrom?: string; dateTo?: string }
    >({
      query: ({ entityType, page = 1, limit = 10, dateFrom, dateTo }) => {
        const params = new URLSearchParams({
          entity_type: entityType,
          event_type: "BULK_OPERATION",
          page: String(page),
          limit: String(limit),
        });
        if (dateFrom) params.append("date_from", dateFrom);
        if (dateTo) params.append("date_to", dateTo);
        return {
          url: `/audit-log?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.DTEF],
    }),
    getDtefImportHistoryUnified: builder.query<
      DtefImportHistoryUnifiedResponse,
      DtefImportHistoryQuery
    >({
      query: ({
        page = 1,
        limit = 10,
        type = "all",
        dateFrom,
        dateTo,
        actorEmail,
        fileName,
        status = "all",
        orderBy = "created_at",
        orderDir = "desc",
      }) => {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
          type,
          status,
          order_by: orderBy,
          order_dir: orderDir,
        });
        if (dateFrom) params.append("date_from", dateFrom);
        if (dateTo) params.append("date_to", dateTo);
        if (actorEmail) params.append("actor_email", actorEmail);
        if (fileName) params.append("file_name", fileName);
        return {
          url: `/dtef/import-history?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.DTEF],
    }),
    getBatchDetails: builder.query<
      BatchDetailsResponse,
      { batchId: string; type: "pix" | "payment" }
    >({
      query: ({ batchId, type }) => ({
        url: `/dtef/batch/${batchId}/details?type=${type}`,
        method: "GET",
      }),
    }),
    getBatchStatuses: builder.mutation<
      { statuses: Record<string, { activeCount: number; isActive: boolean }> },
      { batchIds: string[]; type: "pix" | "payment" }
    >({
      query: (body) => ({
        url: `/dtef/batch-statuses`,
        method: "POST",
        body,
      }),
    }),
    deleteDtefBatch: builder.mutation<
      { message: string; deletedCount: number },
      { batchId: string; type: "pix" | "payment" }
    >({
      query: ({ batchId, type }) => ({
        url: `/dtef/batch/${batchId}?type=${type}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        Tags.DTEF,
        Tags.STORE_CONCILIATION,
        Tags.CAN_CLOSE_CONCILIATION,
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useImportDtefFileMutation,
  useImportPixDtefFileMutation,
  useCheckDtefDuplicateMutation,
  useCheckPixDtefDuplicateMutation,
  useGetDtefImportHistoryQuery,
  useGetDtefImportHistoryUnifiedQuery,
  useDeleteDtefBatchMutation,
  useGetBatchStatusesMutation,
  useLazyGetBatchDetailsQuery,
} = dtefImportApi;
